// Wallet provider integration — `window.ethereum` only (no vendor
// SDK dependency).
//
// The owner-login flow does:
//   1. `eth_requestAccounts` — surface the operator's connected
//      address.
//   2. `personal_sign` (or `eth_signTypedData_v4` when the BE
//      challenge carries a typed-data envelope) — sign the
//      challenge the BE issued.
//   3. POST the signature back to the BE; receive a session token.
//
// Step 2's exact shape (text vs. EIP-712) is determined by the
// BE's challenge response. This file does NOT assume — the caller
// inspects the challenge response and dispatches accordingly.

export class WalletProviderUnavailableError extends Error {
  constructor() {
    super(
      "No EVM wallet provider detected. Install MetaMask / Rabby / Frame and reload.",
    );
  }
}

export interface EthRequestArgs {
  method: string;
  params?: unknown[];
}

export interface EvmProvider {
  request<T = unknown>(args: EthRequestArgs): Promise<T>;
  isMetaMask?: boolean;
  isRabby?: boolean;
  isFrame?: boolean;
  /** EIP-1193 event surface. Optional because a provider is not required to
   *  implement it and `src/auth/provider-events.ts` degrades to no
   *  subscription rather than throwing — but every injected wallet this
   *  console supports does. */
  on?: (event: string, handler: (...args: unknown[]) => void) => void;
  removeListener?: (event: string, handler: (...args: unknown[]) => void) => void;
}

export function getEvmProvider(): EvmProvider {
  const w = window as unknown as { ethereum?: EvmProvider };
  // An object at `window.ethereum` without an EIP-1193 `request` is not an EVM
  // provider, whatever put it there.
  if (!w.ethereum || typeof w.ethereum.request !== "function") {
    throw new WalletProviderUnavailableError();
  }
  return w.ethereum;
}

export async function requestAccount(provider: EvmProvider): Promise<string> {
  const accts = await provider.request<string[]>({ method: "eth_requestAccounts" });
  if (!Array.isArray(accts) || accts.length === 0) {
    throw new Error("Wallet returned no account.");
  }
  return accts[0]!.toLowerCase();
}

export async function personalSign(
  provider: EvmProvider,
  message: string,
  address: string,
): Promise<string> {
  return await provider.request<string>({
    method: "personal_sign",
    params: [message, address],
  });
}

/**
 * Canonical EIP-712 domain-type ordering. Per the spec, fields are
 * encoded in this exact order when present; absent fields are omitted
 * entirely. Mirrors what viem's `recoverTypedDataAddress` reconstructs
 * on the gao-id-worker side — keep the two in lock-step.
 */
const EIP712_DOMAIN_FIELDS: ReadonlyArray<{ name: string; type: string }> = [
  { name: "name", type: "string" },
  { name: "version", type: "string" },
  { name: "chainId", type: "uint256" },
  { name: "verifyingContract", type: "address" },
  { name: "salt", type: "bytes32" },
];

/**
 * Build the EIP712Domain `types[]` list from the keys actually present
 * in `domain`. Exported so the regression test can pin this to the
 * gao-id-worker BE's `buildAdminLoginTypedData` shape.
 */
export function buildEip712DomainTypes(
  domain: Readonly<Record<string, unknown>>,
): Array<{ name: string; type: string }> {
  return EIP712_DOMAIN_FIELDS.filter((f) => domain[f.name] !== undefined).map((f) => ({
    name: f.name,
    type: f.type,
  }));
}

/**
 * EIP-712 v4 signing wrapper. Enriches the caller-supplied `typedData`
 * with an explicit `EIP712Domain` types[] entry derived from the keys
 * present in `typedData.domain`, when one is not already provided.
 *
 * Why this enrichment matters:
 *   The gao-id-worker `/v2/admin/owner/auth/challenge` response carries
 *   `typedData.types = { AdminLogin: [...] }` only — no `EIP712Domain`
 *   field. The EIP-712 spec requires `EIP712Domain` declared alongside
 *   the primary type, but wallet implementations diverge on the
 *   missing-field fallback:
 *     * viem (BE recovery path) builds the type list from the keys
 *       present in `domain` — 3 fields for our case
 *       (name, version, chainId).
 *     * MetaMask and other wallet UIs may fall back to a default
 *       4- or 5-field domain (name, version, chainId,
 *       verifyingContract, salt) and encode `verifyingContract = 0x0`
 *       even though BE never included it.
 *   When the fallback shapes diverge, the wallet signs digest H_W
 *   while the BE recovers using H_BE ≠ H_W, and ECDSA recovery yields
 *   a pseudorandom address — never the actual signer. The dev/test
 *   incident on `0x64cc…1d55` / `op-cb-2026` exhibited this exactly:
 *   the gao-id-worker audit row (after PR #131 surfaced
 *   `recoveredAddress`) showed `0x76410ebc…` with zero on-chain
 *   history — a tell-tale random-recovery artifact.
 *
 *   By declaring `EIP712Domain` explicitly and matching it to the
 *   3-field shape BE recomputes, every conforming wallet now signs
 *   the same digest BE recovers against. No BE auth change, no
 *   signature-verification relaxation.
 *
 *   If the caller has already populated `types.EIP712Domain`, this
 *   wrapper does NOT overwrite it — caller wins.
 */
export async function signTypedDataV4(
  provider: EvmProvider,
  typedData: unknown,
  address: string,
): Promise<string> {
  const enriched = enrichTypedDataWithEip712Domain(typedData);
  return await provider.request<string>({
    method: "eth_signTypedData_v4",
    params: [address, JSON.stringify(enriched)],
  });
}

/**
 * Pure helper: returns a new typedData object whose `types.EIP712Domain`
 * is populated when (and only when) the caller did not already provide
 * one. Domain field list is derived from the keys present in
 * `typedData.domain`. Inputs that are not the expected shape pass
 * through unchanged so unrelated callers are not affected. Exported
 * for the regression test.
 */
export function enrichTypedDataWithEip712Domain(typedData: unknown): unknown {
  if (typedData === null || typeof typedData !== "object") return typedData;
  const td = typedData as Record<string, unknown>;
  const domain = td["domain"];
  const types = td["types"];
  if (
    domain === null ||
    typeof domain !== "object" ||
    Array.isArray(domain) ||
    types === null ||
    typeof types !== "object" ||
    Array.isArray(types)
  ) {
    return typedData;
  }
  const typesObj = types as Record<string, unknown>;
  if (Array.isArray(typesObj["EIP712Domain"])) {
    return typedData;
  }
  const domainFields = buildEip712DomainTypes(domain as Record<string, unknown>);
  return {
    ...td,
    types: {
      EIP712Domain: domainFields,
      ...typesObj,
    },
  };
}

// ── Chain detection — read from the wallet, never switched ──────────

/**
 * Base Sepolia identifiers.
 *
 * This comment used to read "the Admin Web ONLY supports Base Sepolia", which is
 * no longer true and was part of how the login screen came to treat 84532 as a
 * requirement. It is the chain the dev/test escrow and signer-skeleton are bound
 * to, and the chain GaoSafe authority is read on — NOT a chain the operator's
 * wallet has to be on. The login-chain rule is `isAllowedLoginChain` in config.
 */
export const BASE_SEPOLIA_CHAIN_ID = 84532;

/**
 * The EVM chain the wallet is on, as EIP-1193 `eth_chainId` reports it: a
 * `0x`-prefixed hex quantity naming a positive chain id. Anything else — a bare
 * `0x`, a decimal, a CAIP-2 string, a value past 2^53 — is not an EVM chain id
 * and throws, so a caller can never bind a signature to NaN.
 */
export async function getCurrentChainId(provider: EvmProvider): Promise<number> {
  const raw = await provider.request<string>({ method: "eth_chainId" });
  if (typeof raw !== "string" || !/^0x[0-9a-fA-F]+$/.test(raw)) {
    throw new Error(`wallet returned unexpected chainId shape: ${String(raw)}`);
  }
  const id = parseInt(raw, 16);
  if (!Number.isSafeInteger(id) || id <= 0) {
    throw new Error(`wallet returned unexpected chainId shape: ${raw}`);
  }
  return id;
}

// REMOVED: `ChainSwitchUserRejectedError` and `switchToBaseSepolia`.
//
// They asked the wallet to move to Base Sepolia — `wallet_switchEthereumChain`,
// falling back to `wallet_addEthereumChain` — because the console once treated
// 84532 as the chain an operator had to be on to log in.
//
// Under the ratified chain law that requirement does not exist. The wallet
// ADDRESS is the Admin login identity; the connected chain is protocol metadata
// binding the SIWE nonce, and every chain on the canonical allowlist is a valid
// chain to sign from. The GaoSafe authority chain is read server-side and is
// never a constraint on the browser.
//
// Deleted rather than left unreferenced: a helper whose entire purpose is the
// one action the login flow must never take is not dead code, it is a loaded
// footgun waiting to be re-wired by someone reading its name and assuming it is
// needed. An operator who wants a different network changes it in their wallet.

